import axios, { AxiosResponse } from 'axios';
import type {
  Order,
  SystemHealth,
  SystemMetrics,
  AgentMetrics,
  AgentHealth,
  Workflow,
  SimulatorEvent,
  EventSummary,
  IssueData,
  SystemType,
  AgentType,
  IssueType,
  ApiResponse,
  PaginatedResponse
} from '@/types';

// Determine API base URL based on environment
export const getApiBaseUrl = (): string => {
  // If VITE_API_URL is set (Vercel/cloud), use it
  if (import.meta.env.VITE_API_URL) {
    return `${import.meta.env.VITE_API_URL}/api/v1`;
  }
  // In production (nginx proxy), use relative path
  if (window.location.hostname !== 'localhost') {
    return '/nexus/api/v1';
  }
  // Development mode - direct backend connection
  return 'http://localhost:8100/api/v1';
};

// Base URL without /v1 suffix (for ServiceNow routes)
export const getApiRootUrl = (): string => {
  if (import.meta.env.VITE_API_URL) {
    return `${import.meta.env.VITE_API_URL}/api`;
  }
  if (window.location.hostname !== 'localhost') {
    return '/nexus/api';
  }
  return 'http://localhost:8100/api';
};

// Simple axios instance - NO CIRCUIT BREAKER SPAM
const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Minimal logging - NO SPAM
api.interceptors.request.use(
  (config) => {
    // Minimal logging only for debugging
    // Request logging disabled
    return config;
  },
  (error) => Promise.reject(error)
);

// Simple error handling - NO SPAM
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only log actual errors, not every failure
    // Error logging disabled
    return Promise.reject(error);
  }
);

// Health Check API
export const healthApi = {
  check: async (): Promise<any> => {
    const response = await api.get('/health');
    return response.data;
  },
};

// Orders API
export const ordersApi = {
  list: async (params?: { limit?: number; offset?: number; status?: string }): Promise<PaginatedResponse<Order>> => {
    const response = await api.get('/orders', { params });
    return response.data;
  },

  get: async (orderId: string): Promise<Order> => {
    const response = await api.get(`/orders/${orderId}`);
    return response.data;
  },

  create: async (orderData: Partial<Order>): Promise<Order> => {
    const response = await api.post('/orders', orderData);
    return response.data;
  },

  update: async (orderId: string, orderData: Partial<Order>): Promise<Order> => {
    const response = await api.put(`/orders/${orderId}`, orderData);
    return response.data;
  },

  delete: async (orderId: string): Promise<void> => {
    await api.delete(`/orders/${orderId}`);
  },

  analyze: async (orderId: string): Promise<any> => {
    const response = await api.post(`/orders/${orderId}/analyze`);
    return response.data;
  },

  getAuditTrail: async (orderId?: string): Promise<any> => {
    if (orderId) {
      const response = await api.get(`/orders/${orderId}/audit-trail`);
      return response.data;
    } else {
      // Get global audit trail for all orders
      const response = await api.get('/orders/audit-trail');
      return response.data;
    }
  },

  clearAuditTrail: async (): Promise<any> => {
    const response = await api.delete('/orders/audit-trail/clear');
    return response.data;
  },

  getDashboardMetrics: async (): Promise<any> => {
    const response = await api.get('/orders/metrics/dashboard');
    return response.data;
  },
};

// Simulators API
export const simulatorsApi = {
  getHealth: async (): Promise<Record<SystemType, SystemHealth> & { event_bus: any }> => {
    const response = await api.get('/simulators/health');
    return response.data;
  },

  getMetrics: async (): Promise<Record<SystemType, SystemMetrics> & { events: EventSummary }> => {
    const response = await api.get('/simulators/metrics');
    return response.data;
  },

  reset: async (): Promise<Record<string, any>> => {
    const response = await api.post('/simulators/reset');
    return response.data;
  },

  getSystemInfo: async (systemType: SystemType): Promise<any> => {
    const response = await api.get(`/simulators/${systemType}`);
    return response.data;
  },

  // System Orders
  createOrder: async (systemType: SystemType, orderData: any): Promise<any> => {
    const response = await api.post(`/simulators/${systemType}/orders`, orderData);
    return response.data;
  },

  listOrders: async (systemType: SystemType, params?: { status?: string; customer_email?: string }): Promise<any[]> => {
    const response = await api.get(`/simulators/${systemType}/orders`, { params });
    return response.data;
  },

  getSystemOrders: async (systemType: SystemType, params?: { limit?: number }): Promise<any> => {
    const response = await api.get(`/simulators/${systemType}/orders`, { params });
    return response.data;
  },

  getOrder: async (systemType: SystemType, orderId: string): Promise<any> => {
    const response = await api.get(`/simulators/${systemType}/orders/${orderId}`);
    return response.data;
  },

  updateOrder: async (systemType: SystemType, orderId: string, updateData: any): Promise<any> => {
    const response = await api.put(`/simulators/${systemType}/orders/${orderId}`, updateData);
    return response.data;
  },

  deleteOrder: async (systemType: SystemType, orderId: string): Promise<void> => {
    await api.delete(`/simulators/${systemType}/orders/${orderId}`);
  },

  // Issue Management
  injectIssue: async (systemType: SystemType, orderId: string, issueData: { issue_type: IssueType; parameters?: any }): Promise<IssueData> => {
    const response = await api.post(`/simulators/${systemType}/orders/${orderId}/issues`, issueData);
    return response.data;
  },

  resolveIssue: async (systemType: SystemType, issueId: string): Promise<void> => {
    await api.post(`/simulators/${systemType}/issues/${issueId}/resolve`);
  },

  // Events
  getEvents: async (params?: {
    event_type?: string;
    system_type?: string;
    order_id?: string;
    limit?: number;
  }): Promise<SimulatorEvent[]> => {
    const response = await api.get('/simulators/events', { params });
    return response.data;
  },

  getEventsSummary: async (hours = 24): Promise<EventSummary> => {
    const response = await api.get('/simulators/events/summary', { params: { hours } });
    return response.data;
  },

  // System-specific endpoints
  gkpos: {
    getInventory: async (): Promise<any> => {
      const response = await api.get('/simulators/gkpos/inventory');
      return response.data;
    },
    
    restockInventory: async (restockData: { product_id: string; quantity: number }): Promise<void> => {
      await api.post('/simulators/gkpos/inventory/restock', restockData);
    },
    
    getDailySales: async (): Promise<any> => {
      const response = await api.get('/simulators/gkpos/sales/daily');
      return response.data;
    },
  },

  sapEcc: {
    getCustomers: async (): Promise<any> => {
      const response = await api.get('/simulators/sap_ecc/customers');
      return response.data;
    },
    
    getMaterials: async (): Promise<any> => {
      const response = await api.get('/simulators/sap_ecc/materials');
      return response.data;
    },
    
    getFinancialSummary: async (): Promise<any> => {
      const response = await api.get('/simulators/sap_ecc/financial/summary');
      return response.data;
    },
    
    getSalesSummary: async (): Promise<any> => {
      const response = await api.get('/simulators/sap_ecc/sales/summary');
      return response.data;
    },
  },

  hybris: {
    getCustomers: async (): Promise<any> => {
      const response = await api.get('/simulators/hybris/customers');
      return response.data;
    },
    
    getCatalog: async (): Promise<any> => {
      const response = await api.get('/simulators/hybris/catalog');
      return response.data;
    },
    
    getEcommerceMetrics: async (): Promise<any> => {
      const response = await api.get('/simulators/hybris/ecommerce/metrics');
      return response.data;
    },
  },
};

// Agents API
export const agentsApi = {
  getHealth: async (): Promise<{
    orchestrator_id: string;
    status: 'healthy' | 'degraded';
    active_workflows: number;
    agents: Record<AgentType, AgentHealth>;
    performance: any;
    timestamp: string;
  }> => {
    const response = await api.get('/agents/health');
    return response.data;
  },

  getStatus: async (): Promise<{
    orchestrator_id: string;
    active_workflows: Workflow[];
    performance_metrics: any;
    agents: Record<AgentType, { agent_id: string; status: string; metrics: AgentMetrics }>;
    timestamp: string;
  }> => {
    const response = await api.get('/agents/status');
    return response.data;
  },

  getMetrics: async (): Promise<{
    orchestrator_metrics: any;
    agent_metrics: Record<AgentType, AgentMetrics>;
    timestamp: string;
  }> => {
    const response = await api.get('/agents/metrics');
    return response.data;
  },

  resetMetrics: async (): Promise<void> => {
    await api.post('/agents/metrics/reset');
  },

  listAgents: async (): Promise<any> => {
    const response = await api.get('/agents/');
    return response.data;
  },

  getAgentActivities: async (agentType: string): Promise<any> => {
    const response = await api.get(`/agents/${agentType}/activities`);
    return response.data;
  },

  getAgentDetails: async (agentType: AgentType): Promise<any> => {
    const response = await api.get(`/agents/${agentType}`);
    return response.data;
  },

  clearAllActivities: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete('/agents/activities/clear');
    return response.data;
  },

  updateAgentConfig: async (agentType: AgentType, config: Record<string, any>): Promise<void> => {
    await api.put(`/agents/${agentType}/config`, config);
  },

  executeSingleAgent: async (agentType: AgentType, request: {
    order_id: string;
    system_state: Record<string, any>;
    context?: Record<string, any>;
    previous_results?: any[];
  }): Promise<any> => {
    const response = await api.post(`/agents/${agentType}/execute`, request);
    return response.data;
  },

  // Workflows
  executeWorkflow: async (request: {
    order_id: string;
    system_state: Record<SystemType, any>;
    context?: Record<string, any>;
  }): Promise<{
    message: string;
    workflow_id: string;
    status: string;
    results: any;
    performance: any;
    started_at: string;
    completed_at?: string;
  }> => {
    const response = await api.post('/agents/workflows/execute', request);
    return response.data;
  },

  listWorkflows: async (params?: { status?: string; limit?: number }): Promise<{
    workflows: Workflow[];
    total_count: number;
  }> => {
    const response = await api.get('/agents/workflows', { params });
    return response.data;
  },

  getWorkflowDetails: async (workflowId: string): Promise<Workflow & { results: any; errors: any[] }> => {
    const response = await api.get(`/agents/workflows/${workflowId}`);
    return response.data;
  },

  cancelWorkflow: async (workflowId: string): Promise<void> => {
    await api.post(`/agents/workflows/${workflowId}/cancel`);
  },

  testWorkflow: async (): Promise<any> => {
    const response = await api.post('/agents/test/workflow');
    return response.data;
  },

  getAgentEvents: async (params?: {
    event_type?: string;
    agent_type?: string;
    limit?: number;
  }): Promise<{ events: any[]; total_count: number }> => {
    const response = await api.get('/agents/events', { params });
    return response.data;
  },
};

export default api;
